<template>
  <v-container fluid v-if="finishedLoading && bookableItem" class="pa-0">
    <ApplicationWrapper
      @onButtonBackClick="handleBackButtonClick"
      :headerBackTooltip="
        itemType === TYPE_COURSES ? $t('overview.details_header_backToCourseDetails') : $t('overview.details_header_backToHallMeetings')
      "
      :headerImage="bookableItem.image"
      :headerTitle="bookableItem.title">
      <template slot="details" v-if="this.bookableItem.price > 0.0">
        <v-dialog v-model="confirmDirectPayment" width="300px" persistent absolute="absolute">
          <v-card>
            <v-card-title class="text-h5"> {{ $t('payment.info_direct_payment_title') }} </v-card-title>
            <v-card-text>
<div v-html="$t('payment.info_direct_payment')" />

            </v-card-text>
            <v-card-actions>
              <v-btn @click="handleDirectPaymentDialogDecline"> {{ $t('payment.info_direct_payment_btn_no') }} </v-btn>
              <v-spacer></v-spacer>
              <v-btn @click="hasAcceptedDirectPaymentDialog = true"> {{ $t('payment.info_direct_payment_btn_continue') }} </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-list two-line class="nomargin-firstchild nomargin-lastchild">
          <Detail :title="'Bitte wählen Sie eine Zahlungsmethode aus.'" :subTitle="'Es können Gebühren anfallen.'" class="text-center" />
          <Detail
            :append-icon="paymentOptions.length > 1 ? 'someValue' : ''"
            :prependIcon="'mdi-cash-multiple'"
            v-for="(paymentOption, index) of paymentOptions.filter((paymentOption) => paymentOption.Name != PAYMENTOPTION_KLARNA)"
            :key="'paymentOption' + index">
            <!--
           appendIcon needs a value to display right "column" of the list item 
           may be a hacky solution..
           in the future the Details component could have an optional boolean
        -->
            <template slot="content-override">
              <v-list-item-content class="align-center row-height elevation-2 rounded" style="overflow: hidden; position: relative;">
                <!-- overlay to indicate that the paymentoption isnt selected -->
                <v-overlay
                  absolute="absolute"
                  :color="loading.renderingPayPalButton ? 'grey' : 'grey lighten-2'"
                  :value="selectedPaymentOption !== paymentOption.Name || loading.renderingPayPalButton"
                  @click="handlePaymentSelection(paymentOption.Name)" />

                <v-row align="start" justify="center" class="px-2" style="text-wrap: balance; ">
                  <!-- payment option names -->
                  <v-col cols="4">
                    <!-- invoice payment -->
                    <p v-if="paymentOption.Name == PAYMENTOPTION_DEFAULT" >
                      {{ $t('payment.paymentOptionInvoice') }}
                    </p>
                    <!-- paypal payment -->
                    <p v-else-if="paymentOption.Name == PAYMENTOPTION_PAYPAL">
                      {{ $t('payment.paymentOptionPaypal') }}
                    </p>
                    <!-- klarna 
                  <p v-else-if="paymentOption.Name == PAYMENTOPTION_KLARNA"> {{ $t('payment.paymentOptionKlarna') }} </p>
                  -->
                  </v-col>

                  <v-spacer />

                  <!-- fee -->
                  <v-col cols="3" v-if="!isNoWs(getFeeString(paymentOption.Name))">
                    {{ $t('payment.fee') }}
                    <br />
                    {{ getFeeString(paymentOption.Name) }}
                  </v-col>

                  <!-- no fee / club pays fees -->
                  <v-col cols="3" v-else>
                    {{ $t('payment.fee') }}
                    <br />
                    {{ $t('common.label_none') }}
                  </v-col>

                  <!--  -->
                  <v-col cols="4">
                    {{ $t('payment.final_amount') }}
                    <br />
                    {{ getFinalAmountString(paymentOption.Name) }}
                  </v-col>
                </v-row>
              </v-list-item-content>
            </template>
            <template slot="actionarea">
              <v-radio-group :value="selectedPaymentOption" @change="handlePaymentSelection(paymentOption.Name)">
                <v-radio :value="paymentOption.Name" />
              </v-radio-group>
            </template>
          </Detail>
        </v-list>
      </template>
      <!-- case: free booking -->
      <template slot="details" v-if="this.bookableItem.price == 0.0">
        <v-container style="height: 400px">
          <v-row class="fill-height" align-content="center" justify="center">
            <v-col class="text-subtitle-1 text-center" cols="12">
              {{ $t('payment.booking_free') }}
            </v-col>
            <v-col cols="6">
              <v-progress-linear color="primary" indeterminate rounded height="6"></v-progress-linear>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <!-- booking buttons -->
      <template slot="actionbuttons" v-if="this.bookableItem.price > 0.0">
        <v-btn
          v-if="selectedPaymentOption == PAYMENTOPTION_DEFAULT"
          :loading="loading.invoicePaymentButton"
          @click="handleBookViaInvoice"
          color="primary">
          {{ $t('payment.paymentOptionInvoice') }}
        </v-btn>
        <div v-if="selectedPaymentOption == PAYMENTOPTION_PAYPAL" :id="PAYPAL_BUTTON_ID" />
        <!-- <div v-if="selectedPaymentOption == PAYMENTOPTION_KLARNA" :id="KLARNA_BUTTON_ID" /> -->
      </template>
    </ApplicationWrapper>
  </v-container>
  <v-container v-else style="height: 400px">
    <v-row class="fill-height" align-content="center" justify="center">
      <v-col class="text-subtitle-1 text-center" cols="12">
        {{ $t('payment.loading_Methods') }}
      </v-col>
      <v-col cols="6">
        <v-progress-linear color="primary" indeterminate rounded height="6"></v-progress-linear>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// api
import { Api } from '@/api/apiAccess.ts';
import { loadScript } from '@paypal/paypal-js';

// vuex
import { mapGetters } from 'vuex';
import { GET_SESSIONDATA } from '@/store/storeMutator';

// mixins
import Constants from '@/mixins/constants.js';
import Helpers from '@/mixins/helpers.js';
import Booking from '@/mixins/booking.js';

// components
import ApplicationWrapper from '@/components/controls/ApplicationWrapper.vue';
import Detail from '@/components/controls/Detail.vue';

export class BookableItem {
  /**
   * @param {String} image base64 image of course/hall
   * @param {String} title name of course/hall
   * @param {String} subtitle designation of course/hall
   * @param {Number} price booking price of course/hall meeting
   * @param {Object} bookingObject course or hall object
   */
  constructor(image, title, subtitle, price, id, bookingObject) {
    this.image = image;
    this.title = title;
    this.subtitle = subtitle;
    this.price = price;
    this.id = id;
    this.bookingObject = bookingObject;
  }
}

export class PayPalOrderItem {
  constructor(value, name, description, quantity, category) {
    this.value = value;
    this.name = name;
    this.description = description;
    this.quantity = quantity;
    this.category = category;
  }
}

export default {
  name: 'PaymentDialog',

  emits: ['onPaymentSuccess', 'onButtonBackClick'],

  mixins: [Constants, Helpers, Booking],

  components: {
    ApplicationWrapper,
    Detail,
  },

  props: {
    bookableItem: Object,
    itemIdToPayFor: String,
    itemType: {
      type: String,
      default: undefined,
    },
  },

  data() {
    return {
      finishedLoading: false,
      paymentOptions: [],
      orderItem: undefined,
      selectedPaymentOption: undefined,
      paypalOrderItem: undefined,
      showPaymentInfoDialog: false,
      hasAcceptedDirectPaymentDialog: false,
      errors: [],
      loading: {
        invoicePaymentButton: false,
        renderingPayPalButton: false,
      },
    };
  },

  async mounted() {
    // get payment methods
    this.paymentOptions = (await Api.HaKuBu.haKuBuGetPaymentOptions(this.SessionData.club.clubId)).data;

    if (this.paymentOptions === null || this.paymentOptions === undefined) {
      this.handleBackButtonClick();
      this.$root.snackbar.show(this.$root.snackbars.genericError);
      return;
    }

    // extract names of the payment methods
    var availPaymentOptions = this.paymentOptions.map((paymentOption) => paymentOption.Name);

    // if the booking is free, we dont need the user to select an payment method
    if (this.bookableItem.price == 0.0) {
      this.finishedLoading = true;
      this.selectedPaymentOption = this.PAYMENTOPTION_DEFAULT;
      this.handleBookViaInvoice();
    }

    // add paypal payment option
    if (availPaymentOptions.includes(this.PAYMENTOPTION_PAYPAL)) {
      let paymentOptionPaypal = this.paymentOptions.filter((paymentOption) => paymentOption.Name == this.PAYMENTOPTION_PAYPAL)[0];

      this.paypalOrderItem = new PayPalOrderItem(
        this.getPriceWithFee(this.PAYMENTOPTION_PAYPAL),
        this.bookableItem.title,
        this.bookableItem.id,
        1,
        'DIGITAL_GOODS',
      );

      let paypalClientId = paymentOptionPaypal.CredentialUser;

      try {
        // load paypal js sdk
        this.paypal = await loadScript({
          'client-id': paypalClientId,
          currency: 'EUR',
        });
      } catch (error) {
        console.error('failed to load the PayPalJs SDK script', error);
      }
    }

    this.finishedLoading = true;
    this.selectedPaymentOption = this.PAYMENTOPTION_DEFAULT;
  },

  methods: {
    /** returns the payment option object found by name */
    paymentOptionObj(paymentOption) {
      return this.paymentOptions.filter((paymentOpt) => paymentOpt.Name === paymentOption)[0];
    },
    // #region eventhandlers
    /** handles the click on the 'Abbrechen'-button in the direct payment confirm dialog */
    handleDirectPaymentDialogDecline() {
      this.showPaymentInfoDialog = false;
      this.hasAcceptedDirectPaymentDialog = false;
      this.handlePaymentSelection(this.PAYMENTOPTION_DEFAULT);
    },
    /** handles the click on the back button */
    handleBackButtonClick() {
      this.$emit('onButtonBackClick');
    },

    /** handles the selection of a payment option */
    async handlePaymentSelection(paymentOption) {
      if (paymentOption == this.PAYMENTOPTION_PAYPAL || paymentOption == this.PAYMENTOPTION_KLARNA) {
        this.showPaymentInfoDialog = true;
      }

      this.loading.renderingPayPalButton = true;
      this.selectedPaymentOption = paymentOption;

      if (paymentOption == this.PAYMENTOPTION_PAYPAL && this.paypal) {
        // wait for next tick, otherwise the paypal div wouldnt be found
        await this.$nextTick();
        await this.renderPayPalButton();
      }

      this.loading.renderingPayPalButton = false;
      var whatToDisplay = paymentOption == this.PAYMENTOPTION_DEFAULT ? this.$t('payment.paymentOptionInvoice') : paymentOption;
      this.$root.snackbar.show(this.$root.snackbars.paymentOptionChanged(whatToDisplay));
    },
    // #endregion

    // #region invoice
    async handleBookViaInvoice() {
      this.loading.invoicePaymentButton = true;

      await this.book(this.PAYMENTOPTION_DEFAULT, '');

      this.loading.invoicePaymentButton = false;
    },
    // #endregion

    // #region paypal
    /** renders the paypal payment button */
    async renderPayPalButton() {
      try {
        var payPalButton = await this.paypal.Buttons({
          fundingSource: this.paypal.FUNDING.PAYPAL, // we only want the default "Pay with PayPal"-button
          createOrder: this.createPayPalOrder,
          onApprove: this.handlePayPalApproval,
          onCancel: this.onCancel,
          onError: this.handleError,
          style: {
            // --> https://developer.paypal.com/docs/multiparty/checkout/standard/customize/buttons-style-guide/
            layout: 'vertical', // vertical | horizontal
            color: 'blue', // gold | blue | silver | white | black
            shape: 'rect', // rect | pill
            height: 36, // 25 - 55 | i guess pixels (v-buttons height = 36px)
            label: 'paypal', // paypal | checkout | buynow | pay | installment
            tagline: 'false', // true (not allowed for vertical layout) | false
          },
        });

        if (payPalButton.isEligible()) payPalButton.render('#' + this.PAYPAL_BUTTON_ID);
      } catch (error) {
        console.error('failed to render the PayPal buttons', error);
      }
    },

    /** creates a new paypal order */
    async createPayPalOrder(data, actions) {
      // api param
      var PayPalCreateOrderInput = {
        Items: [this.paypalOrderItem],
        CurrencyCode: 'EUR',
      };

      // create a paypal order in cvp
      await Api.HaKuBu.haKuBuCreatePaypalOrder(this.SessionData.club.clubId, PayPalCreateOrderInput);

      return actions.order.create({
        purchase_units: [
          {
            amount: {
              currency_code: 'EUR',
              value: this.paypalOrderItem.value,
            },
            description: this.bookableItem.title + (this.bookableItem.subtitle ? ' | ' + this.stripHtml(this.bookableItem.subtitle) : ''),
          },
        ],
      });
    },

    async handlePayPalApproval(data, actions) {
      var captureResult = (await Api.HaKuBu.haKuBuCapturePaypalOrder(this.SessionData.club.clubId, { OrderId: data.orderID })).data;

      if (captureResult === null || captureResult === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      if (captureResult.OrderId == 0) {
        this.$root.snackbar.show(this.$root.snackbars.paymentPayPalCaptureError);
        return;
      }

      await this.book(this.PAYMENTOPTION_PAYPAL, captureResult);

      return await actions.order.capture();
    },

    onCancel(data, actions) {},

    handleError(error) {},
    // #endregion

    /** returns the fee string depending on data and club settings */
    getFeeString(paymentOption) {
      var paymentOptionObj = this.paymentOptionObj(paymentOption);
      var percentageFee = parseFloat(paymentOptionObj.PercentageFee);
      var basicFee = Number(paymentOptionObj.BasicFee);

      if (paymentOptionObj.ClubPaysFee) return this.$t('payment.club_pays');

      if (percentageFee == 0 && basicFee == 0) return '';

      if (percentageFee > 0 && basicFee > 0) return this.$n(percentageFee, 'decimal') + ' % + ' + this.$n(basicFee, 'currency');
    },

    getFinalAmountString(paymentOption) {
      var priceWithFee = this.getPriceWithFee(paymentOption);

      if (priceWithFee == 0) {
        return this.$t('common.label_free');
      }

      if (priceWithFee > 0) {
        return this.$n(priceWithFee, 'currency', 'de-DE');
      }
    },

    // #endregion
    /** returns whether the given paymentoption can be shown */
    canShowPaymentOption(paymentOptionToCheck) {
      var paymentOptionObj = this.paymentOptionObj(paymentOptionToCheck);

      if (paymentOptionObj !== undefined && paymentOptionObj) return true;
      return false;
    },

    /** returns the price with fees */
    getPriceWithFee(paymentName) {
      // function borrowed from VereinsZeit :)
      // -> https://cvpnet.visualstudio.com/CVPnet%20React%20App/_versionControl?path=%24/CVPnet%20React%20App/Main/src/screens/payment/payment.screen.js
      var feeCalculation = (price, basicFee, percentageFee) => {
        return parseFloat(Math.round(((100 * (basicFee + price)) / (100 - percentageFee)) * 100));
      };
      var paymentOption = this.paymentOptionObj(paymentName);

      var basicFee = parseFloat(paymentOption.BasicFee);
      var percentageFee = parseFloat(paymentOption.PercentageFee);
      var price = parseFloat(this.bookableItem.price);

      if (!paymentOption && paymentOption.ClubPaysFee) return price;
      if (basicFee == 0 && percentageFee == 0) return price;

      if (paymentName == this.PAYMENTOPTION_DEFAULT || paymentName == this.PAYMENTOPTION_KLARNA) {
        return feeCalculation(price, basicFee, percentageFee);
      } else if (paymentName == this.PAYMENTOPTION_PAYPAL) {
        return feeCalculation(price, basicFee, percentageFee) / 100;
      }

      return price;
    },

    /**
     * shortcut for booking courses or halls
     *
     * checks the itemType
     */
    async book(paymentOption, reference) {
      if (this.itemType === this.TYPE_COURSES) await this.bookCourse(paymentOption, reference);
      else if (this.itemType === this.TYPE_HALLS) await this.bookHall(paymentOption, reference);
    },

    /** books a course
     * @param {String} reference paypal order reference
     */
    async bookCourse(paymentOption, reference) {
      var ClubCourseSaveBookingData = {
        CourseId: this.bookableItem.id,
        PaymentInfo: null
      };

      if (paymentOption != this.PAYMENTOPTION_DEFAULT) {
        ClubCourseSaveBookingData.PaymentInfo = {
          Reference: reference,
          System: paymentOption,
          Amount: this.paypalOrderItem
            ? this.paypalOrderItem.value
              ? this.paypalOrderItem.value
              : this.getPriceWithFee(paymentOption)
            : this.getPriceWithFee(paymentOption),
        };
      }

      /*
          before the booking can be completed, a possible reservation must be removed
          otherwise the database will contain incorrect values

          --> if a booking is made on a reserved course, the reservation is set to false but never saved in the database,
              therefore a booked course is considered as reserved AND booked

              for this reason the participant is neither shown in CVP nor in the hall-/course booking

              in such a case, the corresponding database entry must be deleted manually...
        */
      if (this.bookableItem.bookingObject.reserved) {
        var reservationCancellationData = (await Api.HaKuBu.haKuBuRemoveCourseReservation(this.SessionData.club.clubId, this.bookableItem.id)).data;

        // error(code) handling
        if (reservationCancellationData === null || reservationCancellationData === undefined) {
          this.$root.snackbar.show(this.$root.snackbars.genericError);
          return;
        }

        // member was not found in course reservations
        if (reservationCancellationData === -1) this.$root.snackbar.show(this.$root.snackbars.reservationRemoveNoCourseOrMemberNotFound);

        // course isnt reseved
        if (reservationCancellationData === -2) this.$root.snackbar.show(this.$root.snackbars.reservationRemoveNoReservation);

        // in case of an error, return
        if (reservationCancellationData != 0) return;
      }

      // book course
      var bookingData = (await Api.HaKuBu.haKuBuPostSaveCourseBooking(this.SessionData.club.clubId, ClubCourseSaveBookingData)).data;

      // error(code) handling
      if (bookingData === null || bookingData === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      // course is full
      if (bookingData === -1) this.$root.snackbar.show(this.$root.snackbars.courseBookingAddCourseIsFull);

      // course already booked
      if (bookingData === -2) this.$root.snackbar.show(this.$root.snackbars.courseBookingAddCourseAleadyBooked);

      // in case of an error, return
      if (bookingData != 0) return;

      // update course
      await this.updateCourse(this.bookableItem.id);

      // determine the amount of the payed fees
      var feeAmount = this.getPriceWithFee(paymentOption) - parseFloat(this.bookableItem.price);
      if (feeAmount < 0) feeAmount = 0;

      this.$emit('onPaymentSuccess', paymentOption, this.bookableItem.price, this.itemType, this.bookableItem.title, feeAmount);

      this.$root.snackbar.show(this.$root.snackbars.courseBookingAddSuccess);
    },

    /** books a hall
     *
     * @param {String} reference paypal/klarna order reference
     */
    async bookHall(paymentOption, reference) {
      var HallAppointment = {
        HallId: this.bookableItem.id,
        Date: this.bookableItem.bookingObject.date,
        From: this.bookableItem.bookingObject.selectedMeeting.From,
        To: this.bookableItem.bookingObject.selectedMeeting.To,
        FullTime: this.bookableItem.bookingObject.selectedMeeting.FullTime,
        Description: '',
        Comment: 'Buchung durch Hallen-/Kursbuchung (im Web)',
        Amount: 1,
        PaymentInfo: null,
      };

      if (paymentOption != this.PAYMENTOPTION_DEFAULT) {
        HallAppointment.PaymentInfo = {
          Reference: reference,
          System: paymentOption,
          Amount: this.paypalOrderItem
            ? this.paypalOrderItem.value
              ? this.paypalOrderItem.value
              : this.getPriceWithFee(paymentOption)
            : this.getPriceWithFee(paymentOption),
        };
      }

      // book hall
      var bookingResult = await Api.HaKuBu.haKuBuHallBooking(this.SessionData.club.clubId, HallAppointment);

      // error(code) handling
      if (bookingResult.data === null || bookingResult.data === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
      }

      // hall not found
      if (bookingResult.status === 404) this.$root.snackbar.show(this.$root.snackbars.hallBookingHallNotFound);

      // hall booking is disabled
      if (bookingResult.status === 409) this.$root.snackbar.show(this.$root.snackbars.hallBookingHallBookingDisabled);

      // no free meetings
      if (bookingResult.status === 500) this.$root.snackbar.show(this.$root.snackbars.hallBookingNoFreeMeeting);

      // success
      if (bookingResult.status === 200) {
        // determine the amount of the payed fees
        var feeAmount = this.getPriceWithFee(paymentOption) - parseFloat(this.bookableItem.price);
        if (feeAmount < 0) feeAmount = 0;

        this.$emit('onPaymentSuccess', paymentOption, this.bookableItem.price, this.itemType, this.bookableItem.title, feeAmount);

        // update course
        await this.updateHall(this.bookableItem.id);

        this.$root.snackbar.show(this.$root.snackbars.hallBookingAddSuccess);
      }
    },
  },

  computed: {
    ...mapGetters({ SessionData: GET_SESSIONDATA }),

    confirmDirectPayment() {
      return this.showPaymentInfoDialog && !this.hasAcceptedDirectPaymentDialog;
    },
  },
};
</script>

<style scoped>
.v-list-item__title > * {
  text-wrap: balance;
}
</style>

